import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';

import { So5FixtureEvent, Sport } from '#src/__generated__/globalTypes.ts';
import { SEARCH_PARAMS } from '#src/components/search/InstantSearch/types.tsx';
import { useConfigContext } from '#src/contexts/config/index.ts';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { useDeck } from '#src/hooks/decks/useDeck.tsx';
import { useQueryString } from '#src/hooks/useQueryString.ts';
import { clearAll } from '#src/hooks/useVirtualToggle.ts';
import { joinFiltersWithOr, toFilterLiteral } from '#src/lib/algolia.ts';
import type { StatsMode } from '#src/lib/playerSearchView.ts';

import { SearchCardsContext } from './index.tsx';
import { useCompetitionEligibilityFilterValue } from './useCompetitionEligibilityFilterValue.ts';
import { useFavoriteFilterValue } from './useFavoriteFilterValue.ts';
import { useFixtureFilterValue } from './useFixtureFilterValue.ts';
import { useNotInLineupFilterValue } from './useNotInLineupFilterValue.ts';
import { useProbablePitchersFilterValue } from './useProbablePitchersFilterValue.ts';
import { useStatsModeFilterValue } from './useStatsModeFilterValue.ts';
import { useWatchlistFilterValue } from './useWatchlistFilterValue.ts';

type Props = {
  children: ReactNode;
  commonMarket?: boolean;
  editableLists?: boolean;
  galleryOwnerSlug?: string;
  legacyFilters?: boolean;
  manageFavoriteFilter?: boolean;
  sealed?: boolean;
};

export const SearchCardsProvider = ({
  children,
  commonMarket,
  editableLists,
  galleryOwnerSlug,
  legacyFilters = true,
  manageFavoriteFilter = true,
  sealed,
}: Props) => {
  const { sport } = useSportContext();
  const [competitionEligibilityFilter, setCompetitionEligibilityFilter] =
    useState<string | false>(
      useQueryString(SEARCH_PARAMS.COMPETITION_ELIGIBILITY) ?? false
    );

  const [statsMode, setStatsMode] = useState<string | false>(
    useQueryString(SEARCH_PARAMS.STATS_MODE) ?? false
  );

  const [gameweekFilter, setGameweekFilter] = useState<string | false>(
    useQueryString(SEARCH_PARAMS.GAMEWEEK_FILTER) ?? false
  );
  const [dailyFilter, setDailyFilter] = useState<string | false>(
    useQueryString(SEARCH_PARAMS.DAILY_FILTER) ?? false
  );
  const [probablePitchersFilter, setProbablePitchersFilter] = useState<
    string | false
  >(useQueryString(SEARCH_PARAMS.PROBABLE_PITCHERS_FILTERS) ?? false);
  const [customDecksFilter, setCustomDecksFilter] = useState<string | false>(
    useQueryString(SEARCH_PARAMS.DECK) ?? false
  );

  const { deck } = useDeck({
    sport: sport || Sport.FOOTBALL,
    name: customDecksFilter || undefined,
    skip: !editableLists,
  });
  const deckTokens = deck?.cards.nodes;

  const [favoriteFilter, setFavoriteFilter] = useState<boolean>(
    Boolean(useQueryString(SEARCH_PARAMS.FAVORITE_FILTER))
  );
  const [notInLineUpFilter, setNotInLineUpFilter] = useState<boolean>(
    Boolean(useQueryString(SEARCH_PARAMS.NOT_IN_LINEUP))
  );
  const [nonPlayableCards, setNonPlayableCards] = useState<boolean>(
    Boolean(useQueryString(SEARCH_PARAMS.NON_PLAYABLE_CARDS))
  );
  const [legend, setLegend] = useState<boolean>(
    Boolean(useQueryString(SEARCH_PARAMS.LEGEND))
  );
  const [watchlistFilter, setWatchlistFilter] = useState<string | false>(
    useQueryString(SEARCH_PARAMS.WATCHLIST) ?? false
  );

  const { promotionalEvents } = useConfigContext();
  const [promotion, setPromotion] = useState<string | false>(
    useQueryString(SEARCH_PARAMS.PROMOTION) ?? false
  );
  const [promotionalEvent, setPromotionalEvent] = useState<string | false>(
    useQueryString(SEARCH_PARAMS.PROMOTIONAL_EVENT) ?? false
  );
  const [duplicatedKey, setDuplicatedKey] = useState<string | false>(
    useQueryString(SEARCH_PARAMS.DUPLICATED_KEY) ?? false
  );
  const [rawAlgoliaFilters] = useState<string | false>(
    useQueryString(SEARCH_PARAMS.ALGOLIA_FILTERS) ?? false
  );

  const competitionEligibilityFilterValue =
    useCompetitionEligibilityFilterValue(
      competitionEligibilityFilter || undefined
    );
  const gameweekFilterValue = useFixtureFilterValue({
    filterValue: gameweekFilter || undefined,
    eventType: So5FixtureEvent.CLASSIC,
  });

  const dailyFilterValue = useFixtureFilterValue({
    filterValue: dailyFilter || undefined,
    eventType: So5FixtureEvent.DAILY,
  });

  const probablePitchersFilterValue = useProbablePitchersFilterValue({
    probablePitchersFilterValue: probablePitchersFilter || undefined,
    skip: sport !== Sport.BASEBALL,
  });

  const { watchlistFilterValue, watchlistFilterTitle } =
    useWatchlistFilterValue({
      filterValue: watchlistFilter || undefined,
    });

  const favoriteFilterValue = useFavoriteFilterValue({
    skip: !manageFavoriteFilter || !legacyFilters || !favoriteFilter,
  });
  const { notInLineupFilterValue } = useNotInLineupFilterValue({
    skip: !notInLineUpFilter,
  });

  const statsModeFilterValue = useStatsModeFilterValue(statsMode as StatsMode);

  const promotionalEventsFilters = useMemo(() => {
    const activePromotionalEvent = promotionalEvents.find(
      ({ slug }) => slug === promotionalEvent
    );

    return activePromotionalEvent?.algoliaFilters;
  }, [promotionalEvents, promotionalEvent]);

  const filters = useMemo(
    () =>
      [
        customDecksFilter &&
          joinFiltersWithOr([
            ...(deckTokens?.map(
              ({ slug }) => `slug:"${toFilterLiteral(slug)}"`
            ) || []),
            `custom_decks.name:"${toFilterLiteral(customDecksFilter)}"`,
          ]),
        competitionEligibilityFilter && competitionEligibilityFilterValue,
        gameweekFilter &&
          (legacyFilters
            ? gameweekFilterValue
            : `playing_next:${gameweekFilter}`),
        dailyFilter &&
          (legacyFilters ? dailyFilterValue : `playing_next:${dailyFilter}`),
        manageFavoriteFilter &&
          legacyFilters &&
          favoriteFilter &&
          favoriteFilterValue,
        probablePitchersFilter && probablePitchersFilterValue,
        !!watchlistFilter && watchlistFilterValue,
        notInLineUpFilter && notInLineupFilterValue,
        promotionalEvent && promotionalEventsFilters,
        rawAlgoliaFilters,
        sealed !== undefined && (sealed ? 'sealed=1' : 'NOT sealed=1'),
        statsModeFilterValue,
      ].filter(Boolean),
    [
      customDecksFilter,
      deckTokens,
      competitionEligibilityFilter,
      competitionEligibilityFilterValue,
      gameweekFilter,
      legacyFilters,
      gameweekFilterValue,
      dailyFilter,
      dailyFilterValue,
      manageFavoriteFilter,
      favoriteFilter,
      favoriteFilterValue,
      probablePitchersFilter,
      probablePitchersFilterValue,
      watchlistFilter,
      watchlistFilterValue,
      notInLineUpFilter,
      notInLineupFilterValue,
      promotionalEvent,
      promotionalEventsFilters,
      rawAlgoliaFilters,
      sealed,
      statsModeFilterValue,
    ]
  );

  const clearFilters = () => {
    setCompetitionEligibilityFilter(false);
    setGameweekFilter(false);
    setDailyFilter(false);
    setCustomDecksFilter(false);
    setFavoriteFilter(false);
    setNotInLineUpFilter(false);
    setNonPlayableCards(false);
    setPromotion(false);
    setProbablePitchersFilter(false);
    setPromotionalEvent(false);
    setDuplicatedKey(false);
    setWatchlistFilter(false);
    clearAll();
  };

  const value = useMemo(
    () => ({
      filters,
      competitionEligibilityFilter,
      setCompetitionEligibilityFilter,
      gameweekFilter,
      setGameweekFilter,
      dailyFilter,
      setDailyFilter,
      probablePitchersFilter,
      setProbablePitchersFilter,
      customDecksFilter,
      setCustomDecksFilter,
      favoriteFilter,
      setFavoriteFilter,
      notInLineUpFilter,
      setNotInLineUpFilter,
      nonPlayableCards,
      setNonPlayableCards,
      legend,
      setLegend,
      promotion,
      setPromotion,
      clearFilters,
      commonMarket,
      editableLists,
      galleryOwnerSlug,
      promotionalEvent,
      setPromotionalEvent,
      duplicatedKey,
      setDuplicatedKey,
      sealed,
      statsMode,
      setStatsMode,
      watchlistFilter: !!watchlistFilter && {
        id: watchlistFilterValue,
        title: watchlistFilterTitle,
      },
      setWatchlistFilter,
    }),
    [
      filters,
      competitionEligibilityFilter,
      gameweekFilter,
      dailyFilter,
      probablePitchersFilter,
      customDecksFilter,
      favoriteFilter,
      notInLineUpFilter,
      nonPlayableCards,
      legend,
      promotion,
      commonMarket,
      editableLists,
      galleryOwnerSlug,
      promotionalEvent,
      duplicatedKey,
      sealed,
      statsMode,
      watchlistFilter,
      watchlistFilterValue,
      watchlistFilterTitle,
    ]
  );

  return <SearchCardsContext value={value}>{children}</SearchCardsContext>;
};
